import React, { useState } from "react";
import { 
  Button, 
  Box, 
  IconButton, 
  TextField,
  Paper,
  Typography,
  Fade
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";

const DynamicSearchBar = ({ fields, onSearch, onClose }) => {
  
  const [searchValues, setSearchValues] = useState({});

  const handleInputChange = (fieldKey, value) => {
    setSearchValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleSearch = () => {
    const processedSearchValues = { ...searchValues };
    
    Object.keys(processedSearchValues).forEach(key => {
      if ((key === 'fromDate' || key === 'toDate') && processedSearchValues[key]) {
        if (processedSearchValues[key] instanceof Date) {
          const year = processedSearchValues[key].getFullYear();
          const month = String(processedSearchValues[key].getMonth() + 1).padStart(2, '0');
          const day = String(processedSearchValues[key].getDate()).padStart(2, '0');
          processedSearchValues[key] = `${year}-${month}-${day}`;
        }
      }
    });
    
    console.log('Sending search values:', processedSearchValues);
    onSearch(processedSearchValues);
  };

  const handleClearSearch = () => {
    setSearchValues({});
    onSearch({});
  };

  const isDateField = (fieldKey) => {
    return fieldKey === 'fromDate' || fieldKey === 'toDate';
  };

  const renderField = (field) => {
    if (isDateField(field.key)) {
      return (
        <TextField
          label={field.name}
          type="date"
          value={searchValues[field.key] ? new Date(searchValues[field.key]).toISOString().split('T')[0] : ""}
          onChange={(e) => handleInputChange(field.key, e.target.value ? new Date(e.target.value) : null)}
          InputLabelProps={{ shrink: true }}
          size="small"
          placeholder={field.placeholder || "בחר תאריך..."}
          sx={{ 
            width: '100%',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#fff',
              height: '42px',
              fontSize: '0.9375rem',
              transition: 'all 0.2s ease',
              '& fieldset': {
                borderColor: '#d1d5db'
              },
              '&:hover fieldset': {
                borderColor: '#9ca3af'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#6b7280',
                borderWidth: '2px'
              }
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: '#6b7280'
            }
          }}
        />
      );
    }
    
    return (
      <TextField
        label={field.name}
        variant="outlined"
        size="small"
        fullWidth
        value={searchValues[field.key] || ''}
        onChange={(e) => handleInputChange(field.key, e.target.value)}
        placeholder={field.placeholder || `הזן ${field.name}...`}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#fff',
            height: '42px',
            fontSize: '0.9375rem',
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: '#d1d5db'
            },
            '&:hover fieldset': {
              borderColor: '#9ca3af'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6b7280',
              borderWidth: '2px'
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.9375rem',
            fontWeight: 500,
            color: '#6b7280'
          }
        }}
      />
    );
  };

  return (
    <Fade in timeout={300}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            borderBottom: '2px solid #e5e7eb',
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon sx={{ fontSize: 20, color: '#6b7280' }} />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1.125rem', 
                color: '#111827'
              }}
            >
              חיפוש מתקדם
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ 
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              color: '#6b7280',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#e5e7eb',
                color: '#111827'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'end', 
            gap: 2, 
            flexWrap: 'wrap' 
          }}>
            {/* Search Fields */}
            {fields && fields.map((field, index) => (
              <Box 
                key={field.key || index} 
                sx={{ 
                  minWidth: '200px', 
                  flex: '1 1 200px',
                  maxWidth: '300px'
                }}
              >
                {renderField(field)}
              </Box>
            ))}
            
            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              minWidth: 'fit-content',
              ml: 'auto'
            }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearSearch}
                sx={{
                  minWidth: 100,
                  height: 42,
                  px: 3,
                  borderRadius: '8px',
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  borderColor: '#d1d5db',
                  color: '#374151',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb'
                  }
                }}
              >
                נקה
              </Button>

              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  minWidth: 100,
                  height: 42,
                  px: 4,
                  borderRadius: '8px',
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0c3d8a 100%)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                חפש
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Fade>
  );
};

export default DynamicSearchBar;